/* eslint-disable react/prop-types */
/**
 * V9.10 — "Je participe !"
 *
 * Bouton + popover pour qu'un utilisateur connecté déclare la participation
 * d'une de ses fiches athlètes à un événement du calendrier.
 *
 * - Si l'utilisateur n'a aucune fiche athlète : redirige vers /creer-ma-fiche.
 * - Si l'utilisateur a une seule fiche : toggle direct.
 * - Si plusieurs fiches : popover de sélection.
 * - Affiche aussi le compteur de participants + mini-avatars des participants
 *   publiés (récupérés via l'endpoint `/calendar/{id}/participants`).
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Check, Loader2, Sparkles, Users, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";

function initials(name) {
  return (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export function ParticipateButton({ eventId, eventTitle, size = "sm", variant = "default", className = "", onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [myState, setMyState] = useState({ athletes: [], participating: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [participants, setParticipants] = useState({ items: [], count: 0, loading: true });

  // Charge la liste publique des participants pour le compteur + avatars
  const loadParticipants = useCallback(async () => {
    try {
      const { data } = await api.get(`/calendar/${eventId}/participants`);
      setParticipants({ items: data.items || [], count: data.count || 0, loading: false });
    } catch {
      setParticipants({ items: [], count: 0, loading: false });
    }
  }, [eventId]);

  // Charge l'état de participation pour les athlètes possédés par l'utilisateur
  const loadMyState = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/calendar/mine-participations");
      const athletes = data.athletes || [];
      const participating = athletes
        .filter((a) => (data.participations?.[a.id] || []).includes(eventId))
        .map((a) => a.id);
      setMyState({ athletes, participating });
    } catch {
      setMyState({ athletes: [], participating: [] });
    }
  }, [eventId, user]);

  useEffect(() => { loadParticipants(); }, [loadParticipants]);
  useEffect(() => { loadMyState(); }, [loadMyState]);

  // Action : déclarer la participation d'un athlète précis
  const doParticipate = async (athleteId) => {
    setBusy(true);
    try {
      await api.post(`/calendar/${eventId}/participate`, { athlete_id: athleteId });
      toast.success("Tu participes à cet événement.");
      await Promise.all([loadParticipants(), loadMyState()]);
      onChange?.();
    } catch (e) {
      showError(e, { fallback: "Échec de l'inscription." });
    } finally {
      setBusy(false);
      setPickerOpen(false);
    }
  };

  // Action : annuler la participation d'un athlète précis
  const doUnparticipate = async (athleteId) => {
    setBusy(true);
    try {
      await api.delete(`/calendar/${eventId}/participate`, { params: { athlete_id: athleteId } });
      toast.success("Participation annulée.");
      await Promise.all([loadParticipants(), loadMyState()]);
      onChange?.();
    } catch (e) {
      showError(e, { fallback: "Échec de l'annulation." });
    } finally {
      setBusy(false);
      setPickerOpen(false);
    }
  };

  // Comportement du bouton principal selon le contexte
  const handleClick = () => {
    if (!user) {
      // Non connecté : redirige vers login avec retour
      const next = encodeURIComponent(window.location.pathname);
      navigate(`/login?next=${next}`);
      return;
    }
    if (myState.athletes.length === 0) {
      // Pas de fiche athlète : oriente vers la création
      toast.info("Crée d'abord ta fiche athlète pour t'inscrire à un événement.");
      navigate("/creer-ma-fiche");
      return;
    }
    if (myState.athletes.length === 1) {
      const a = myState.athletes[0];
      const isIn = myState.participating.includes(a.id);
      if (isIn) doUnparticipate(a.id); else doParticipate(a.id);
      return;
    }
    // Plusieurs athlètes → popover de sélection
    setPickerOpen(true);
  };

  // Label du bouton principal
  const isParticipatingAny = myState.participating.length > 0;
  const label = isParticipatingAny ? "Je participe ✓" : "Je participe !";
  const Icon = isParticipatingAny ? Check : Sparkles;

  return (
    <div className={`flex items-center flex-wrap gap-2 ${className}`} data-testid="participate-wrap">
      {/* Bouton principal */}
      {myState.athletes.length <= 1 ? (
        <Button
          type="button"
          size={size}
          variant={isParticipatingAny ? "outline" : variant}
          onClick={handleClick}
          disabled={busy}
          data-testid={`event-${eventId}-participate-btn`}
          className={isParticipatingAny
            ? "border-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/10"
            : "bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Icon className="h-3.5 w-3.5 mr-1.5" />}
          {label}
        </Button>
      ) : (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size={size}
              variant={isParticipatingAny ? "outline" : variant}
              disabled={busy}
              data-testid={`event-${eventId}-participate-btn`}
              className={isParticipatingAny
                ? "border-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/10"
                : "bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Icon className="h-3.5 w-3.5 mr-1.5" />}
              {label}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-2" data-testid="participate-picker">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2 py-1.5">
              Choisis ta fiche athlète
            </div>
            <ul className="space-y-1">
              {myState.athletes.map((a) => {
                const inAlready = myState.participating.includes(a.id);
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => (inAlready ? doUnparticipate(a.id) : doParticipate(a.id))}
                      className="w-full flex items-center gap-2 p-2 rounded hover:bg-muted text-left disabled:opacity-50"
                      data-testid={`participate-pick-${a.id}`}
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarImage src={resolveAthletePhoto(a)} />
                        <AvatarFallback className="text-[10px]">{initials(a.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{a.full_name}</div>
                        {a.main_discipline && (
                          <div className="text-[11px] text-muted-foreground truncate">{a.main_discipline}</div>
                        )}
                      </div>
                      {inAlready
                        ? <X className="h-4 w-4 text-destructive shrink-0" />
                        : <Check className="h-4 w-4 text-[hsl(var(--aito-lagoon))] shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>
      )}

      {/* Compteur participants + dialog liste */}
      {participants.count > 0 && (
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground rounded-full border border-dashed border-border/70 px-2.5 py-1 transition"
              data-testid={`event-${eventId}-participants-btn`}
              aria-label={`${participants.count} sportif·ve·s participent`}
            >
              {/* Avatar group */}
              <div className="flex -space-x-1.5">
                {participants.items.slice(0, 3).map((a) => (
                  <Avatar key={a.id} className="h-5 w-5 border border-background">
                    <AvatarImage src={resolveAthletePhoto(a)} />
                    <AvatarFallback className="text-[8px]">{initials(a.full_name)}</AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <Users className="h-3 w-3" />
              <span>{participants.count} sportif{participants.count > 1 ? "s" : ""}</span>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid={`event-${eventId}-participants-dialog`}>
            <DialogHeader>
              <DialogTitle className="text-base">Participants confirmés</DialogTitle>
              <DialogDescription className="text-xs">
                {eventTitle ? <>« {eventTitle} » — </> : null}
                {participants.count} sportif{participants.count > 1 ? "s" : ""} déclaré·s.
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
              {participants.items.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/athletes/${a.slug || a.id}`}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted"
                    data-testid={`participant-${a.id}`}
                  >
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={resolveAthletePhoto(a)} />
                      <AvatarFallback className="text-[11px]">{initials(a.full_name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.full_name}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {a.main_discipline && (
                          <Badge variant="outline" className="text-[9px] capitalize">
                            {a.main_discipline.replace(/-/g, " ")}
                          </Badge>
                        )}
                        {(a.commune || a.island) && (
                          <span className="text-[10px] text-muted-foreground truncate">
                            {a.commune || a.island}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
